import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
    Box, Typography, Button, Stack, Grid, TextField, Skeleton, Chip, Divider,
    IconButton,
} from "@mui/material";
import SaveIcon from "@mui/icons-material/SaveOutlined";
import ArrowBackIcon from "@mui/icons-material/ArrowBackOutlined";
import { toast } from "react-toastify";
import AppHeader from "../components/AppHeader";
import AppFooter from "../components/AppFooter";
import api, { apiError, tokens as session } from "../api";

/*
 * The customer's own details.
 *
 * Nothing here is needed to use the product - an account works with an email alone - but
 * an invoice needs a name, a billing address and a GSTIN, and asking for them once here
 * is better than asking at the Razorpay checkout, where a half-filled form loses the sale.
 *
 * The email is shown and not editable. It is the identity every plant presents, so
 * changing it from this page would orphan the tokens issued against it.
 */

const darkField = {
    "& .MuiOutlinedInput-root": {
        color: "#fff", borderRadius: 2,
        bgcolor: "rgba(255,255,255,.04)",
        "& fieldset": { borderColor: "rgba(255,255,255,.16)" },
        "&:hover fieldset": { borderColor: "rgba(255,255,255,.32)" },
        "&.Mui-focused fieldset": { borderColor: "#7BD95F" },
    },
    "& .MuiInputLabel-root": { color: "rgba(255,255,255,.5)" },
    "& .MuiInputLabel-root.Mui-focused": { color: "#7BD95F" },
    "& .MuiFormHelperText-root": { color: "rgba(255,255,255,.4)" },
    "& .Mui-disabled": { WebkitTextFillColor: "rgba(255,255,255,.45)" },

    /*
     * Chrome paints autofilled inputs its own pale yellow-white and ignores
     * `background-color` outright. The only thing it does honour on that element is an
     * inset box-shadow, so the field's own colour is painted with one; without this a
     * remembered name or address comes back as black text on white in the middle of a
     * dark form, which reads as a broken page rather than a filled one.
     */
    "& input:-webkit-autofill": {
        WebkitTextFillColor: "#fff",
        WebkitBoxShadow: "0 0 0 1000px #11231A inset",
        caretColor: "#fff",
        borderRadius: 8,
        transition: "background-color 9999s ease-in-out 0s",
    },
};

// Everything the server lets a customer change, in the order it is asked for.
const BLANK = {
    name: "", mobile: "", designation: "",
    companyName: "", gstin: "",
    address: "", city: "", state: "", pincode: "", country: "India",
};

const sectionTitle = { fontWeight: 700, fontSize: 15.5, color: "#fff" };
const sectionNote = { color: "rgba(255,255,255,.5)", fontSize: 13.5, mt: .4, mb: 2.5 };

export default function Profile() {
    const navigate = useNavigate();
    const [account, setAccount] = useState(null);
    const [form, setForm] = useState(BLANK);
    const [busy, setBusy] = useState(false);

    /*
     * Straight after the code, before they have ever seen the console.
     *
     * Signing up asks for an email and nothing else, so the first thing a new customer
     * needs is to say who they are. Decided once, on load: saving fills the name in, and
     * the page should not change its mind about what it is halfway through.
     */
    const [welcome, setWelcome] = useState(false);

    useEffect(() => {
        api.get("/me")
            .then(({ data }) => {
                setAccount(data.account);
                setWelcome(!data.account.name);
                // Field by field off BLANK, so a value the server has not sent arrives as
                // "" and not undefined - an undefined value turns the input uncontrolled
                // and React then warns on the first keystroke.
                setForm(Object.fromEntries(
                    Object.keys(BLANK).map((k) => [k, data.account[k] ?? BLANK[k]])
                ));
            })
            .catch(() => { session.account.clear(); navigate("/"); });
    }, [navigate]);

    const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

    /*
     * The one field that is not optional.
     *
     * Everything else here can be filled in the day it is needed - an address matters at
     * the first invoice, a GSTIN only to those who have one. A name matters immediately:
     * it is what support and every receipt call this customer, and an email address is
     * not a person's name.
     */
    const nameMissing = !form.name.trim();

    const save = async () => {
        if (nameMissing) {
            toast.error("Please enter your name");
            return;
        }
        setBusy(true);
        try {
            const { data } = await api.patch("/me", form);
            setAccount(data.account);
            if (welcome) {
                // First time through: finish the sign-up by going where they were headed.
                toast.success(`Welcome, ${data.account.name}`);
                navigate("/account");
                return;
            }
            toast.success("Profile saved");
        } catch (e) { toast.error(apiError(e)); }
        setBusy(false);
    };

    const field = (key, label, extra = {}) => (
        <TextField label={label} size="small" fullWidth sx={darkField}
            value={form[key]} onChange={set(key)} {...extra} />
    );

    return (
        <Box sx={{
            minHeight: "100vh", color: "#fff",
            display: "flex", flexDirection: "column",
            background:
                "radial-gradient(900px 520px at 12% 0%, rgba(46,125,32,.32), transparent 62%),"
                + "radial-gradient(760px 540px at 88% 100%, rgba(20,90,60,.28), transparent 64%), #07120B",
        }}>
            <AppHeader account={account} />

            {/* Capped and centred. A form is read one field at a time, and on a wide
                monitor a full-bleed one puts a 600px box around a person's name and
                drags the eye across empty space between the label and the next field. */}
            <Box sx={{ py: { xs: 3, md: 5 }, px: { xs: 2, md: 4 }, width: "100%", maxWidth: 1080, mx: "auto" }}>
                {/* Beside the title rather than only at the bottom of the form: this page is
                    reached from the avatar menu, so the way out should be visible without
                    scrolling past every field first. */}
                <Stack direction="row" spacing={1.5} sx={{ alignItems: "center" }}>
                    {/* No way back on the first visit: there is nothing behind this page
                        yet, and an arrow that leads to a console with a nameless account
                        is an arrow that only makes trouble later. */}
                    {!welcome && (
                        <IconButton onClick={() => navigate("/account")}
                            sx={{
                                color: "rgba(255,255,255,.75)",
                                border: "1px solid rgba(255,255,255,.14)",
                                bgcolor: "rgba(255,255,255,.04)",
                                "&:hover": { bgcolor: "rgba(255,255,255,.09)" },
                            }}>
                            <ArrowBackIcon fontSize="small" />
                        </IconButton>
                    )}
                    <Box>
                        <Typography sx={{ fontWeight: 800, fontSize: 26, letterSpacing: -0.5 }}>
                            {welcome ? "Tell us about you" : "Profile"}
                        </Typography>
                        <Typography sx={{ color: "rgba(255,255,255,.6)", fontSize: 14.5, mt: .3 }}>
                            {welcome
                                ? "Your email is verified. Just your name to finish - the rest can wait."
                                : "Your details, used on invoices and when support needs to reach you."}
                        </Typography>
                    </Box>
                </Stack>
                <Box sx={{ height: 24 }} />

                {!account ? (
                    <Skeleton variant="rectangular" height={420}
                        sx={{ bgcolor: "rgba(255,255,255,.06)", borderRadius: 2 }} />
                ) : (
                    <Box sx={{
                        p: { xs: 2.5, md: 3.5 }, borderRadius: 2,
                        bgcolor: "rgba(255,255,255,.04)", border: "1px solid rgba(255,255,255,.12)",
                    }}>
                        {/* Read-only, and said so plainly: this email is what every plant
                            presents, so it is not a field with a typo to fix here. */}
                        <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}
                            sx={{ alignItems: { sm: "center" }, justifyContent: "space-between", mb: 3 }}>
                            <Box>
                                <Typography sx={{ color: "rgba(255,255,255,.5)", fontSize: 12.5 }}>
                                    Signed in as
                                </Typography>
                                <Typography sx={{ fontWeight: 700, fontSize: 16 }}>{account.email}</Typography>
                            </Box>
                            <Chip size="small" label="Email cannot be changed"
                                sx={{
                                    bgcolor: "rgba(255,255,255,.06)", color: "rgba(255,255,255,.6)",
                                    border: "1px solid rgba(255,255,255,.14)",
                                }} />
                        </Stack>

                        <Divider sx={{ borderColor: "rgba(255,255,255,.1)", mb: 3 }} />

                        <Typography sx={sectionTitle}>About you</Typography>
                        <Typography sx={sectionNote}>Who support should ask for.</Typography>
                        <Grid container spacing={2.5}>
                            <Grid size={{ xs: 12, sm: 6, lg: 4 }}>
                                {field("name", "Full name", {
                                    required: true,
                                    autoFocus: welcome,
                                    error: welcome && nameMissing,
                                })}
                            </Grid>
                            <Grid size={{ xs: 12, sm: 6, lg: 4 }}>{field("mobile", "Mobile number")}</Grid>
                            <Grid size={{ xs: 12, sm: 6, lg: 4 }}>{field("designation", "Designation")}</Grid>
                        </Grid>

                        <Divider sx={{ borderColor: "rgba(255,255,255,.1)", my: 3.5 }} />

                        <Typography sx={sectionTitle}>Company</Typography>
                        <Typography sx={sectionNote}>The name and GSTIN printed on your invoices.</Typography>
                        <Grid container spacing={2.5}>
                            <Grid size={{ xs: 12, sm: 6, lg: 4 }}>{field("companyName", "Company name")}</Grid>
                            <Grid size={{ xs: 12, sm: 6, lg: 4 }}>
                                {field("gstin", "GSTIN", {
                                    helperText: "Leave blank if you are not registered",
                                    inputProps: { style: { textTransform: "uppercase" } },
                                })}
                            </Grid>
                        </Grid>

                        <Divider sx={{ borderColor: "rgba(255,255,255,.1)", my: 3.5 }} />

                        <Typography sx={sectionTitle}>Billing address</Typography>
                        <Typography sx={sectionNote}>Where the invoice goes.</Typography>
                        <Grid container spacing={2.5}>
                            <Grid size={{ xs: 12, lg: 8 }}>
                                {field("address", "Address", { multiline: true, minRows: 2 })}
                            </Grid>
                            <Grid size={{ xs: 12, sm: 6, lg: 4 }}>{field("city", "City")}</Grid>
                            <Grid size={{ xs: 12, sm: 6, lg: 4 }}>{field("state", "State")}</Grid>
                            <Grid size={{ xs: 12, sm: 6, lg: 4 }}>{field("pincode", "PIN code")}</Grid>
                            <Grid size={{ xs: 12, sm: 6, lg: 4 }}>{field("country", "Country")}</Grid>
                        </Grid>

                        <Stack direction="row" spacing={1.5}
                            sx={{ justifyContent: "flex-end", mt: 4 }}>
                            {!welcome && (
                                <Button onClick={() => navigate("/account")}
                                    sx={{ color: "rgba(255,255,255,.65)" }}>Back to tokens</Button>
                            )}
                            <Button variant="contained" startIcon={<SaveIcon />} onClick={save}
                                disabled={busy || nameMissing} sx={{ borderRadius: 999, px: 3 }}>
                                {welcome ? "Continue" : "Save changes"}
                            </Button>
                        </Stack>
                    </Box>
                )}
            </Box>

            <AppFooter />
        </Box>
    );
}
